import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import MessageBubble from "./MessageBubble";
import { MEDIA_REFRESH_AHEAD_MS, dayLabel, mediaLinkExpiresAt } from "./waUtils";

const NEAR_BOTTOM_PX = 120;
const MEDIA_CHECK_INTERVAL_MS = 60 * 1000;
const MEDIA_RETRY_COOLDOWN_MS = 60 * 1000;
const MEDIA_MAX_ATTEMPTS = 3;

/**
 * Scrollable message list with date separators, "load older", auto-scroll to
 * bottom on new messages, and a "new messages" pill when the user has
 * scrolled up. Shared by the inbox thread and the lead/client WhatsApp tab.
 */
const ThreadMessages = ({ conversationKey, items, hasMore, loading, onLoadOlder, onImageClick, onRetry, canRetry, onRefreshMedia, style }) => {
  const scrollRef = useRef(null);
  const atBottomRef = useRef(true);
  const prevLenRef = useRef(0);
  const prevFirstIdRef = useRef(null);
  const prevHeightRef = useRef(0);
  const [newCount, setNewCount] = useState(0);

  const byWamid = useMemo(() => {
    const map = new Map();
    for (const m of items) if (m.wamid) map.set(m.wamid, m);
    return map;
  }, [items]);

  // ── Signed media links expire: refresh them ahead of time, or when one fails to load.
  const mediaAttemptsRef = useRef(new Map()); // id → { at, count }
  const requestMediaRefresh = useCallback((ids) => {
    if (!onRefreshMedia) return;
    const now = Date.now();
    const due = ids.filter((id) => {
      const a = mediaAttemptsRef.current.get(id);
      return !a || (a.count < MEDIA_MAX_ATTEMPTS && now - a.at > MEDIA_RETRY_COOLDOWN_MS);
    });
    if (!due.length) return;
    due.forEach((id) => {
      const a = mediaAttemptsRef.current.get(id);
      mediaAttemptsRef.current.set(id, { at: now, count: (a?.count || 0) + 1 });
    });
    onRefreshMedia(due);
  }, [onRefreshMedia]);

  useEffect(() => {
    if (!onRefreshMedia) return undefined;
    const check = () => {
      const soon = Date.now() + MEDIA_REFRESH_AHEAD_MS;
      const stale = items.filter((m) => {
        const exp = m.media?.url ? mediaLinkExpiresAt(m.media.url) : null;
        return exp && exp < soon;
      }).map((m) => m.id);
      if (stale.length) requestMediaRefresh(stale);
    };
    check();
    const timer = setInterval(check, MEDIA_CHECK_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [items, onRefreshMedia, requestMediaRefresh]);

  // A refreshed link resets the attempt budget for that message.
  useEffect(() => {
    items.forEach((m) => {
      const exp = m.media?.url ? mediaLinkExpiresAt(m.media.url) : null;
      if (exp && exp > Date.now() + MEDIA_REFRESH_AHEAD_MS) mediaAttemptsRef.current.delete(m.id);
    });
  }, [items]);

  const scrollToBottom = useCallback((smooth = false) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
    atBottomRef.current = true;
    setNewCount(0);
  }, []);

  const prevKeyRef = useRef(conversationKey);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Switched to another conversation → treat as a fresh load (jump to bottom).
    if (prevKeyRef.current !== conversationKey) {
      prevKeyRef.current = conversationKey;
      prevLenRef.current = 0;
      prevFirstIdRef.current = null;
      atBottomRef.current = true;
      setNewCount(0);
    }
    const firstId = items[0]?.id || null;
    const prevLen = prevLenRef.current;
    if (prevLen === 0 && items.length) {
      el.scrollTop = el.scrollHeight;
    } else if (firstId !== prevFirstIdRef.current && prevFirstIdRef.current && items.length > prevLen) {
      // Older page prepended — keep the viewport anchored on what the user was reading.
      el.scrollTop = el.scrollHeight - prevHeightRef.current + el.scrollTop;
    } else if (items.length > prevLen) {
      const last = items[items.length - 1];
      if (atBottomRef.current || last?.direction === "outbound" || last?.direction === "internal") {
        el.scrollTop = el.scrollHeight;
      } else {
        setNewCount((n) => n + (items.length - prevLen));
      }
    }
    prevLenRef.current = items.length;
    prevFirstIdRef.current = firstId;
    prevHeightRef.current = el.scrollHeight;
  }, [items, conversationKey]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    if (atBottomRef.current && newCount) setNewCount(0);
    prevHeightRef.current = el.scrollHeight;
    if (el.scrollTop < 40 && hasMore && !loading) onLoadOlder?.();
  };

  let lastDay = "";
  return (
    <>
      <div
        className="wa-thread__scroll"
        ref={scrollRef}
        onScroll={onScroll}
        // Images/videos grow the thread after load — stay pinned to the bottom if we were there.
        onLoadCapture={() => {
          const el = scrollRef.current;
          if (el && atBottomRef.current) el.scrollTop = el.scrollHeight;
        }}
        style={style}
        aria-live="polite"
      >
        {hasMore && (
          <div className="wa-thread__loadmore">
            <button type="button" className="wa-btn wa-btn--sm" disabled={loading} onClick={onLoadOlder}>
              {loading ? "Loading…" : "Load earlier messages"}
            </button>
          </div>
        )}
        {items.map((m) => {
          const day = dayLabel(m.timestamp);
          const showDay = day !== lastDay;
          lastDay = day;
          return (
            <React.Fragment key={m.id}>
              {showDay && <div className="wa-day"><span>{day}</span></div>}
              <MessageBubble
                message={m}
                quoted={m.replyToWamid ? byWamid.get(m.replyToWamid) : null}
                onImageClick={onImageClick}
                onRetry={onRetry}
                canRetry={canRetry}
                onMediaError={onRefreshMedia ? () => requestMediaRefresh([m.id]) : undefined}
              />
            </React.Fragment>
          );
        })}
      </div>
      {newCount > 0 && (
        <button type="button" className="wa-newpill" onClick={() => scrollToBottom(true)}>
          <i className="fa-solid fa-arrow-down" /> {newCount} new message{newCount > 1 ? "s" : ""}
        </button>
      )}
    </>
  );
};

export default ThreadMessages;
